import React, { useEffect, useState } from "react";
import { MessageCircle, Loader2, ChevronRight, Send, ArrowLeft, Trash2, RefreshCw } from "lucide-react";
import Button from "../ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";

const STATUS_OPTIONS = [
  { value: "new", label: "Nuovo" },
  { value: "open", label: "Aperto" },
  { value: "awaiting_user", label: "Attesa utente" },
  { value: "awaiting_admin", label: "Attesa admin" },
  { value: "closed", label: "Chiuso" },
];

const STATUS_STYLE = {
  new: "bg-arcane/20 text-arcane border-arcane",
  open: "bg-gold/20 text-gold border-gold",
  awaiting_user: "bg-gold/20 text-gold border-gold",
  awaiting_admin: "bg-ruby/20 text-ruby border-ruby",
  closed: "bg-muted/20 text-muted-foreground border-border",
};

const CATEGORY_LABELS = {
  general: "Generale",
  billing: "Fatturazione",
  technical: "Tecnico",
  bug: "Bug",
  feature_request: "Feature",
  premium: "Premium",
};

const SupportTicketsTab = () => {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [replying, setReplying] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [internalNote, setInternalNote] = useState(false);

  const fetchTickets = async (status = statusFilter) => {
    try {
      setLoading(true);
      const qs = status ? `?status=${status}` : "";
      const r = await fetch(`${API}/api/admin/support/tickets${qs}`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setTickets(d.tickets || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const openTicket = async (t) => {
    setSelected(t);
    setLoadingDetail(true);
    try {
      const r = await fetch(`${API}/api/support/tickets/${t.ticket_id}`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setSelected(d.ticket);
        setMessages(d.messages || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyBody.trim()) return;
    try {
      setReplying(true);
      const r = await fetch(`${API}/api/support/tickets/${selected.ticket_id}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ body: replyBody.trim(), is_internal_note: internalNote }),
      });
      if (r.ok) {
        setReplyBody("");
        setInternalNote(false);
        await openTicket(selected);
        fetchTickets();
      } else {
        const err = await r.json();
        alert(`❌ ${err.detail || "Errore invio"}`);
      }
    } catch (e) {
      alert("❌ Errore di rete");
    } finally {
      setReplying(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const r = await fetch(`${API}/api/admin/support/tickets/${selected.ticket_id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: newStatus }),
      });
      if (r.ok) {
        setSelected({ ...selected, status: newStatus });
        fetchTickets();
      }
    } catch (e) {
      alert("❌ Errore aggiornamento stato");
    }
  };

  const handleDeleteTicket = async () => {
    if (!window.confirm("Eliminare definitivamente questo ticket e tutti i messaggi?")) return;
    try {
      const r = await fetch(`${API}/api/admin/support/tickets/${selected.ticket_id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (r.ok) {
        setSelected(null);
        setMessages([]);
        fetchTickets();
      }
    } catch (e) {
      alert("❌ Errore eliminazione");
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Loader2 className="w-12 h-12 text-gold animate-spin mx-auto" />
        </CardContent>
      </Card>
    );
  }

  if (selected) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <button onClick={() => { setSelected(null); setMessages([]); }} className="text-xs text-muted-foreground hover:text-gold mb-2 flex items-center">
                <ArrowLeft className="w-3 h-3 mr-1" /> Torna alla lista
              </button>
              <CardTitle data-testid="admin-ticket-subject">{selected.subject}</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                <strong>{selected.user_name}</strong> ({selected.user_email}) · {CATEGORY_LABELS[selected.category] || selected.category}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <select
                value={selected.status}
                onChange={(e) => handleStatusChange(e.target.value)}
                data-testid="admin-ticket-status-select"
                className={`px-3 py-1 rounded-lg text-xs font-heading border ${STATUS_STYLE[selected.status] || STATUS_STYLE.new}`}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              <Button onClick={handleDeleteTicket} variant="ghost" size="sm" data-testid="admin-ticket-delete">
                <Trash2 className="w-4 h-4 text-ruby" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loadingDetail ? (
            <div className="py-8 text-center">
              <Loader2 className="w-8 h-8 mx-auto text-gold animate-spin" />
            </div>
          ) : (
            <>
              <div className="space-y-4 mb-6 max-h-[500px] overflow-y-auto">
                {messages.map((m) => (
                  <div key={m.message_id} className={`flex ${m.sender_role === "admin" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[80%] p-3 rounded-lg border ${
                        m.is_internal_note
                          ? "bg-ruby/10 border-ruby/50"
                          : m.sender_role === "admin"
                          ? "bg-arcane/10 border-arcane/50"
                          : "bg-card border-border"
                      }`}
                      data-testid={`admin-msg-${m.message_id}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-heading ${
                          m.is_internal_note ? "text-ruby" : m.sender_role === "admin" ? "text-arcane" : "text-gold"
                        }`}>
                          {m.is_internal_note ? "🔒 Nota interna" : (m.sender_role === "admin" ? "🛡️ Admin" : `${m.sender_name || "Utente"}`)}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(m.created_at).toLocaleString("it-IT")}
                        </span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                    </div>
                  </div>
                ))}
              </div>

              <form onSubmit={handleReply} className="space-y-3">
                <textarea
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  rows="4"
                  placeholder="Scrivi la risposta..."
                  className="w-full rounded-md bg-input border border-input px-3 py-2 text-sm text-foreground focus:ring-2 focus:ring-ring focus:outline-none"
                  data-testid="admin-reply-body"
                />
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={internalNote}
                      onChange={(e) => setInternalNote(e.target.checked)}
                      data-testid="admin-internal-note"
                    />
                    Nota interna (non visibile all'utente)
                  </label>
                  <Button type="submit" variant="primary" disabled={replying || !replyBody.trim()} data-testid="admin-reply-submit">
                    {replying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Invio...</> : <><Send className="w-4 h-4 mr-2" />Invia</>}
                  </Button>
                </div>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="flex items-center">
            <MessageCircle className="w-5 h-5 mr-2 text-gold" />
            Ticket & Messaggi ({tickets.length})
          </CardTitle>
          <div className="flex gap-2 items-center">
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); fetchTickets(e.target.value); }}
              className="px-3 py-1.5 rounded-lg text-xs bg-background border border-border text-foreground"
              data-testid="admin-tickets-status-filter"
            >
              <option value="">Tutti gli stati</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <Button onClick={() => fetchTickets()} variant="ghost" size="sm">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {tickets.length === 0 ? (
          <div className="py-12 text-center">
            <MessageCircle className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground">Nessun ticket per questo filtro.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tickets.map((t) => (
              <button
                key={t.ticket_id}
                onClick={() => openTicket(t)}
                data-testid={`admin-ticket-item-${t.ticket_id}`}
                className="w-full text-left p-4 border border-border rounded-lg hover:border-gold transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <p className="font-heading text-foreground truncate">{t.subject}</p>
                      <span className={`text-xs px-2 py-0.5 rounded font-heading border ${STATUS_STYLE[t.status] || STATUS_STYLE.new}`}>
                        {STATUS_OPTIONS.find((s) => s.value === t.status)?.label || t.status}
                      </span>
                      {t.unread_by_admin > 0 && (
                        <span className="text-xs px-2 py-0.5 rounded bg-ruby text-white font-heading">
                          {t.unread_by_admin} nuovi
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      <strong>{t.user_name}</strong> ({t.user_email}) · {CATEGORY_LABELS[t.category] || t.category} · {new Date(t.last_message_at).toLocaleString("it-IT")}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground mt-1" />
                </div>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SupportTicketsTab;
